import { type Draft, produce } from "immer";
import { compress, decompress } from "lz-string";

// TODO undo-redo: decide a proper amount for history count
const MAX_HISTORY = 50;

export type UndoableCommitOptions = {
  preventCommit?: boolean;
};

export interface UndoableState<T> {
  past: string[];
  present: T;
  lastCommitedState: string;
  future: string[];
}

/**
 * Compress the state to a string
 * @param state - The state to compress
 * @returns The compressed state
 */
const compressState = <T>(state: T): string => {
  const stateString = JSON.stringify(state);
  return compress(stateString);
};

/**
 * Decompress the state from a string
 * @param compressedState - The compressed state
 * @returns The decompressed state
 */
const decompressState = <T>(compressedState: string): T => {
  const stateString = decompress(compressedState);
  return JSON.parse(stateString) as T;
};

/**
 * Factory function to create an undoable state
 *
 * @param initial - The initial state
 * @returns The undoable state
 */
export const createUndoable = <T>(initial: T): UndoableState<T> => ({
  future: [],
  lastCommitedState: compressState(initial),
  past: [],
  present: initial,
});

/**
 * Commit a field change to the undoable state
 *
 * @param state - The undoable state
 * @param updater - The updater function
 * @param options - The options
 *   - preventCommit: If true, the change will not be committed to history but instead applied to the present state
 * @returns The undoable state
 */
export const commitFieldChange = <T>(
  state: UndoableState<T>,
  updater: (draft: Draft<T>) => void,
  options: UndoableCommitOptions = {},
): UndoableState<T> => {
  // Create a new present using Immer
  const newPresent = produce(state.present, updater);

  // If nothing changed, return the same state
  if (newPresent === state.present) {
    return state;
  }

  // Compress the new state
  const compressedNewPresent = compressState(newPresent);

  if (options.preventCommit) {
    return {
      future: state.future,
      lastCommitedState: state.lastCommitedState,
      past: state.past,
      present: newPresent,
    };
  }

  // Append the current present to the past
  const newPast = [...state.past, state.lastCommitedState];
  if (newPast.length > MAX_HISTORY) {
    newPast.shift();
  }

  return {
    future: [],
    lastCommitedState: compressedNewPresent,
    past: newPast,
    present: newPresent,
  };
};

/**
 * Go back one step in the undo history
 *
 * @param state - The undoable state
 * @returns The undoable state
 */
export const undoFieldChange = <T>(
  state: UndoableState<T>,
): UndoableState<T> => {
  if (state.past.length === 0) return state;
  const previous = state.past[state.past.length - 1];
  const decompressedPrevious = decompressState<T>(previous);
  return {
    future: [compressState(state.present), ...state.future],
    lastCommitedState: previous,
    past: state.past.slice(0, -1),
    present: decompressedPrevious,
  };
};

/**
 * Go forward one step in the undo history
 *
 * @param state - The undoable state
 * @returns The undoable state
 */
export const redoFieldChange = <T>(
  state: UndoableState<T>,
): UndoableState<T> => {
  if (state.future.length === 0) return state;
  const next = state.future[0];
  const decompressedNext = decompressState<T>(next);
  return {
    future: state.future.slice(1),
    lastCommitedState: next,
    past: [...state.past, compressState(state.present)],
    present: decompressedNext,
  };
};
